import { supertypeClass, remote } from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';
import rp from 'request-promise-native';

import { CoverpathCustomer } from '../../classes/coverpath_subclasses/CoverpathCustomer';
import { TwoFactorAuthStatus } from '../../angular/classes/TwoFactorAuthStatus';
import { WebServiceUtils } from '../../../common/js/webservice/WebServiceUtils';
import { LoggerSingleton, Category, PiiLevelEnum } from '../../../common/js/logger/LoggerSingleton';

@supertypeClass({ toClient: true, toServer: true })
export class TFAController extends Everything {
  constructor() {
    super();
  }

  static new() {
    return new TFAController().init();
  }

  init() {
    return this;
  }

  @remote({ on: 'server' })
  publicSendTFACodeToPhone(
    method: string,
    resend: boolean,
    customer: CoverpathCustomer
  ): Promise<TwoFactorAuthStatus | void> {
    return new Promise<TwoFactorAuthStatus | void>((resolve, reject) => {
      if (this.checkStatus(customer) === TwoFactorAuthStatus.locked) {
        reject(TwoFactorAuthStatus.locked);
      } else {
        if (method !== 'sms' && method !== 'voice') {
          LoggerSingleton.getInstance().error(
            {
              module: 'TFAController',
              function: 'publicSendTFACodeToPhone',
              category: Category.request,
              context: {
                piiLevel: PiiLevelEnum.nonPII,
              },
              error: {
                isHumanRelated: false,
              },
            },
            `TFA unsupported method ${method} only sms/phone allowed`
          );
          reject(TwoFactorAuthStatus.unsupported);
        }

        const baseConfig = WebServiceUtils.serviceCfg('TFAService');

        if (!baseConfig || !baseConfig.endpointUrl) {
          LoggerSingleton.getInstance().error(
            {
              module: 'TFAController',
              function: 'publicSendTFACodeToPhone',
              category: Category.request,
              context: {
                piiLevel: PiiLevelEnum.nonPII,
              },
              error: {
                isHumanRelated: false,
              },
            },
            `TFA not configured returning 12345 as the code`
          );
        }
        const baseUrl = baseConfig.endpointUrl;
        let url = `${baseUrl}/${method}?phone=${customer.twoFactorAuth.getPhone()}`;

        const tfaCode = customer.twoFactorAuth.getCode();
        if (resend && tfaCode) {
          url += '&resend-token=' + tfaCode;
        }

        const opts = {
          uri: url,
          json: true,
        };

        return rp(opts)
          .then((response) => {
            this.setCode(response, customer);

            const txn = this.amorphic.begin();
            customer.twoFactorAuth.setDirty(txn);
            this.amorphic.end(txn);
            resolve(TwoFactorAuthStatus.success);
          })
          .catch((err) => {
            const error = {
              isHumanRelated: false,
              name: err.name,
              message: err.message,
              stack: err.stack,
            };
            LoggerSingleton.getInstance().error(
              {
                module: 'TFAController',
                function: 'publicSendTFACodeToPhone',
                category: Category.request,
                error: error,
              },
              'TFA unable to get code from sms provider'
            );
            reject(TwoFactorAuthStatus.no_response);
          });
      }
    });
  }

  @remote({ on: 'server' })
  publicSubmitAndVerifyTFACode(submittedCode: number, customer: CoverpathCustomer): Promise<TwoFactorAuthStatus> {
    return new Promise<TwoFactorAuthStatus>(async (resolve, reject) => {
      if (this.checkStatus(customer) === TwoFactorAuthStatus.locked) {
        reject(TwoFactorAuthStatus.locked);
      }
      const status = this.checkStatus(customer);
      if (status !== TwoFactorAuthStatus.open) {
        reject(status || TwoFactorAuthStatus.not_found);
      } else {
        const isProd = this.amorphic.config.nconf.get('environment') === 'production';
        const code = this.getCode(customer);
        const tfa = customer.twoFactorAuth;
        if (code) {
          const valid = Number(code) === Number(submittedCode) || (!isProd && Number(submittedCode) === 54321);
          if (valid) {
            customer.tfaAuthenticated = true;
            tfa.setStatus(TwoFactorAuthStatus[TwoFactorAuthStatus.verified]);
            const txn = this.amorphic.begin();
            customer.setDirty();
            tfa.setDirty(txn);
            this.amorphic.end(txn);
            resolve(TwoFactorAuthStatus.verified);
          } else {
            this.incrementNumberInvalidAttempts(customer);
            const txn = this.amorphic.begin();
            tfa.setDirty(txn);
            await this.amorphic.end(txn);
            reject(TwoFactorAuthStatus.invalid);
          }
        } else {
          resolve(TwoFactorAuthStatus.pending);
        }
      }
    });
  }

  @remote({ on: 'server' })
  getMaskedTFAPhone(customer: CoverpathCustomer): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      resolve(`( • • • ) • • • - • • ` + customer.twoFactorAuth.getPhone().slice(-2));
    });
  }

  @remote({ on: 'server' })
  async setTFAPhone(phone: string, customer: CoverpathCustomer): Promise<void> {
    customer.twoFactorAuth.phone = phone;
    const txn = this.amorphic.begin();
    customer.twoFactorAuth.setDirty(txn);
    await this.amorphic.end(txn);
  }

  private setCode(code: number, customer: CoverpathCustomer): void {
    const tfa = customer.twoFactorAuth;
    tfa.setCode(code);
    tfa.setNumberInvalidAttempts(0);
    tfa.setStatus(TwoFactorAuthStatus[TwoFactorAuthStatus.open]);
    tfa.setTimeSent(new Date());
  }

  private getCode(customer: CoverpathCustomer): number {
    const tfa = customer.twoFactorAuth;
    const timeout = this.amorphic.config.nconf.get('twoFactorAuthTimeout');
    if (tfa.getCode() && Date.now() - tfa.getTimeSent().getTime() > timeout) {
      tfa.setStatus(TwoFactorAuthStatus[TwoFactorAuthStatus.expired]);
    } else {
      return tfa.getCode();
    }
  }

  private checkStatus(customer: CoverpathCustomer): TwoFactorAuthStatus {
    const tfa = customer.twoFactorAuth;
    let status = TwoFactorAuthStatus[tfa.getStatus()];
    const lockout = this.amorphic.config.nconf.get('twoFactorAuthLockout');
    const timeout = this.amorphic.config.nconf.get('twoFactorAuthTimeout');

    if (
      TwoFactorAuthStatus[tfa.getStatus()] === TwoFactorAuthStatus.locked &&
      Date.now() - tfa.getTimeLockedOut().getTime() > lockout
    ) {
      tfa.setStatus(TwoFactorAuthStatus[TwoFactorAuthStatus.open]);
      status = TwoFactorAuthStatus.open;
    }
    if (
      TwoFactorAuthStatus[tfa.getStatus()] === TwoFactorAuthStatus.open &&
      Date.now() - tfa.getTimeSent().getTime() > timeout
    ) {
      tfa.setStatus(TwoFactorAuthStatus[TwoFactorAuthStatus.expired]);
      status = TwoFactorAuthStatus.expired;
    }

    return status;
  }

  private incrementNumberInvalidAttempts(customer: CoverpathCustomer): void {
    const tfa = customer.twoFactorAuth;
    const maxAttempts = this.amorphic.config.nconf.get('twoFactorAuthMaxAttempts');

    tfa.incrementInvalidAttempts();
    if (tfa.getNumberInvalidAttempts() >= maxAttempts) {
      tfa.setStatus(TwoFactorAuthStatus[TwoFactorAuthStatus.locked]);
      tfa.setTimeLockedOut(new Date());
    }
  }
}
